<script setup>
import { watch, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Editor from 'dashboard/components-next/Editor/Editor.vue';
import ContactNoteItem from 'next/Contacts/ContactsSidebar/components/ContactNoteItem.vue';
import EstadoVazio from 'dashboard/components-next/EstadoVazio.vue';
import EsqueletoLista from 'dashboard/components-next/EsqueletoLista.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const uiFlags = useMapGetter('contactNotes/getUIFlags');
const notesByContact = useMapGetter('contactNotes/getAllNotesByContactId');
const isFetchingNotes = computed(() => uiFlags.value.isFetching);
const isCreatingNote = computed(() => uiFlags.value.isCreating);
const contactId = computed(() => props.contactId);
const noteContent = ref('');
const shouldShowCreateModal = ref(false);
const falhouCarga = ref(false);

const notes = computed(() => {
  if (!contactId.value) return [];
  return notesByContact.value(contactId.value) || [];
});

const getWrittenBy = ({ user } = {}) => {
  const currentUserId = currentUser.value?.id;
  return user?.id === currentUserId
    ? t('CONTACTS_LAYOUT.SIDEBAR.NOTES.YOU')
    : user?.name || t('CONVERSATION.BOT');
};

const openCreateModal = () => {
  if (!contactId.value) return;
  noteContent.value = '';
  shouldShowCreateModal.value = true;
};

const closeCreateModal = () => {
  shouldShowCreateModal.value = false;
  noteContent.value = '';
};

// Falhar ao salvar mantém o texto no editor: antes a rejeição saía solta e
// a pessoa não sabia se a nota tinha ido.
const onAdd = async () => {
  if (!contactId.value || !noteContent.value || isCreatingNote.value) return;
  try {
    await store.dispatch('contactNotes/create', {
      content: noteContent.value,
      contactId: contactId.value,
    });
    closeCreateModal();
  } catch {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.NOTES.SAVE_ERROR'));
  }
};

const onDelete = async noteId => {
  if (!contactId.value || !noteId) return;
  try {
    await store.dispatch('contactNotes/delete', {
      noteId,
      contactId: contactId.value,
    });
  } catch {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.NOTES.DELETE_ERROR'));
  }
};

useKeyboardEvents({
  '$mod+Enter': {
    action: () => shouldShowCreateModal.value && onAdd(),
    allowOnFocusedInput: true,
  },
});

// "Nenhuma nota" e "não consegui carregar as notas" são frases diferentes:
// a segunda não pode parecer a primeira.
const carregar = async id => {
  falhouCarga.value = false;
  try {
    await store.dispatch('contactNotes/get', { contactId: id });
  } catch {
    if (id === contactId.value) falhouCarga.value = true;
  }
};

watch(
  contactId,
  novoId => {
    closeCreateModal();
    if (novoId) carregar(novoId);
  },
  { immediate: true }
);
</script>

<template>
  <div class="cnt">
    <button
      type="button"
      class="advup-btn advup-btn--compacto cnt__nova"
      :disabled="!contactId || isFetchingNotes"
      @click="openCreateModal"
    >
      <i class="i-lucide-notebook-pen" aria-hidden="true" />
      {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.ADD_NOTE') }}
    </button>

    <EsqueletoLista
      v-if="isFetchingNotes"
      :linhas="3"
      :altura="72"
      :rotulo="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.LOADING')"
    />

    <EstadoVazio
      v-else-if="falhouCarga"
      class="cnt__vazio"
      tom="erro"
      icone="i-lucide-wifi-off"
      :titulo="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.LOAD_ERROR')"
    >
      <button
        type="button"
        class="advup-btn advup-btn--compacto"
        @click="carregar(contactId)"
      >
        <i class="i-lucide-rotate-cw" aria-hidden="true" />
        {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.RETRY') }}
      </button>
    </EstadoVazio>

    <!-- Nota apagada recolhe no lugar e as de baixo sobem (24/10/2026); antes sumia seca.
         A lista fica montada mesmo vazia: com `v-if="notes.length"` a ÚLTIMA nota apagada
         levava a lista inteira junto, e a saída não rodava. -->
    <TransitionGroup v-else tag="ul" name="cnt-nota" class="cnt__lista">
      <li
        v-for="(note, i) in notes"
        :key="note.id"
        class="advup-card cnt__nota advup-entra"
        :style="{ '--i': Math.min(i, 10) }"
      >
        <ContactNoteItem
          :note="note"
          :written-by="getWrittenBy(note)"
          allow-delete
          collapsible
          @delete="onDelete"
        />
      </li>
    </TransitionGroup>

    <EstadoVazio
      v-if="!isFetchingNotes && !falhouCarga && !notes.length"
      class="cnt__vazio"
      icone="i-lucide-notebook-pen"
      :titulo="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.EMPTY_TITLE')"
      :dica="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.EMPTY_HINT')"
    />

    <Teleport to="body">
      <Transition name="advup-saida">
        <div
          v-if="shouldShowCreateModal"
          class="advup-modal-fundo"
          @click.self="closeCreateModal"
        >
          <div
            v-modal-acessivel="closeCreateModal"
            :data-rascunho="`contato:${contactId}:nota`"
            class="advup-modal advup-modal--claro advup-modal--teto"
          >
            <div class="advup-modal__topo advup-modal__topo--ambar">
              <!-- Topo claro da Agenda (§5.10), como os outros modais — era o
                   degradê escuro (05/10/2026). -->
              <i
                class="advup-modal__marca i-lucide-notebook-pen"
                aria-hidden="true"
              />
              <span class="advup-modal__selo" aria-hidden="true">
                <i class="i-lucide-notebook-pen" />
              </span>
              <div class="min-w-0 flex-1">
                <h2 class="advup-modal__titulo">
                  {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.ADD_NOTE') }}
                </h2>
                <p class="advup-modal__sub advup-modal__sub--solta">
                  {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.MODAL_SUB') }}
                </p>
              </div>
              <button
                type="button"
                class="advup-modal__fechar"
                :aria-label="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.CLOSE')"
                @click="closeCreateModal"
              >
                <i class="i-lucide-x" />
              </button>
            </div>

            <div class="advup-modal__corpo">
              <!-- Sem contador: com ele ligado, o editor CORTAVA a nota em 200
                   caracteres (padrão do componente) — e o servidor aceita
                   texto longo. Anotação de caso não cabe em 200. -->
              <Editor
                v-model="noteContent"
                focus-on-mount
                :show-character-count="false"
                :placeholder="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.PLACEHOLDER')"
                class="cnt__editor"
              />
            </div>

            <div class="advup-modal__rodape advup-modal__rodape--acoes">
              <span class="advup-nota advup-modal__espaco cnt__atalho">
                {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.SHORTCUT') }}
              </span>
              <button type="button" class="advup-btn" @click="closeCreateModal">
                {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.CANCEL') }}
              </button>
              <button
                type="button"
                class="advup-btn-forte"
                :disabled="!noteContent || isCreatingNote"
                @click="onAdd"
              >
                <i
                  :class="
                    isCreatingNote
                      ? 'i-lucide-loader-2 advup-gira'
                      : 'i-lucide-check'
                  "
                  aria-hidden="true"
                />
                {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.SAVE') }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.cnt {
  display: flex;
  flex-direction: column;
  gap: var(--esp-3);
}

.cnt__nova {
  align-self: flex-start;
}

.cnt__vazio {
  padding: var(--esp-5) var(--esp-2);
}

.cnt__lista {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

/* Vazia, a lista não ocupa vão no painel (ela fica montada para a última nota sair). */
.cnt__lista:empty {
  display: none;
}

.cnt__nota {
  padding: var(--esp-3);
}

/* Três classes: vence o `.advup-entra` da chegada sem depender da ordem das folhas. */
.cnt__lista .cnt-nota-leave-active {
  overflow: hidden;
  pointer-events: none;
  animation: cnt-nota-sai var(--mov-3) var(--mov-curva-saida) forwards;
}

@keyframes cnt-nota-sai {
  from {
    max-height: 24rem;
  }

  to {
    max-height: 0;
    margin-block: 0;
    padding-block: 0;
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cnt__lista .cnt-nota-leave-active {
    animation: none;
  }
}

.cnt__editor {
  min-height: 10rem;
}

.cnt__atalho {
  margin-block: 0;
}
</style>
